import type { UploadFileStatus, UploadState } from '@/types/upload';

// Simple upload state tracker without Redux patterns
let globalUploadState: UploadState = {
  uploads: [],
  isUploading: false,
  overallProgress: 0,
  completedCount: 0,
  totalCount: 0,
};

const listeners = new Set<(state: UploadState) => void>();

// Helper function to calculate progress metrics
function calculateMetrics(uploads: UploadFileStatus[]): Omit<UploadState, 'uploads'> {
  const completedCount = uploads.filter(u => u.status === 'success' || u.status === 'error').length;
  const totalProgress = uploads.reduce((sum, u) => sum + u.progress, 0);
  const overallProgress = uploads.length > 0 ? Math.round(totalProgress / uploads.length) : 0;
  const isUploading = uploads.some(u => u.status === 'uploading' || u.status === 'waiting');

  return {
    completedCount,
    overallProgress,
    isUploading,
    totalCount: uploads.length,
  };
}

// Notify all listeners of state changes
function notifyListeners() {
  listeners.forEach(listener => listener(globalUploadState));
}

// Update state and notify listeners
function updateState(updates: Partial<UploadState>) {
  globalUploadState = { ...globalUploadState, ...updates };
  notifyListeners();
}

// Public API functions
export function addUpload(upload: UploadFileStatus) {
  const newUploads = [...globalUploadState.uploads, upload];
  updateState({
    uploads: newUploads,
    ...calculateMetrics(newUploads),
  });
}

export function updateUpload(id: string, updates: Partial<UploadFileStatus>) {
  const newUploads = globalUploadState.uploads.map(upload =>
    upload.id === id ? { ...upload, ...updates } : upload,
  );

  updateState({
    uploads: newUploads,
    ...calculateMetrics(newUploads),
  });

  // Auto-cleanup when all uploads are complete
  const allComplete = newUploads.every(u => u.status === 'success' || u.status === 'error');
  if (allComplete && newUploads.length > 0) {
    setTimeout(() => clearAllUploads(), 3000);
  }
}

export function removeUpload(id: string) {
  const newUploads = globalUploadState.uploads.filter(upload => upload.id !== id);
  updateState({
    uploads: newUploads,
    ...calculateMetrics(newUploads),
  });
}

export function clearAllUploads() {
  updateState({
    uploads: [],
    isUploading: false,
    overallProgress: 0,
    completedCount: 0,
    totalCount: 0,
  });
}

export function getUploadState(): UploadState {
  return globalUploadState;
}

export function subscribeToUploads(listener: (state: UploadState) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
